import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    getExpandedRowModel,
    getGroupedRowModel,
    getSortedRowModel,
    Row,
    RowSelectionState,
    TableMeta,
    ColumnSort as TanstackColumnSort,
    Updater,
    useReactTable,
    VisibilityState,
} from '@tanstack/react-table';
import { CSSProperties, ReactNode, useMemo, useRef } from 'react';
import { OptimusTextKey, useLocalization } from '@optimus-web/localization';
import { ZIndex } from '@optimus-web/common';
import { Container } from '../Container';
import { EmptyState, EmptyStateProps } from '../EmptyState';
import { LoadingOverlay } from '../helpers/LoadingOverlay';
import { Spinner } from '../Spinner';
import { TableColumnHeaderActions, TablePagination, TableRow, VirtualTableBody } from './components';
import { useTableColumns } from './hooks';
import { PrintStyles } from './styles/print-style';
import {
    TableBody,
    TableCellInner,
    TableContainer,
    TableElement,
    TableHeader,
    TableHeaderCell,
    TableHeaderRow,
    TableWrapper,
    TableFooter,
    TableFooterRow,
    TableFooterCell,
} from './styles/style';
import { TableProvider, useTableContext } from './TableContext';
import { ColumnSort, Order, TableFilters, TableState } from './types';

export interface TableProps<T> {
    data: T[];
    uniqueRowId: keyof T;
    loading?: boolean;
    amountOfRows: number;
    fixedFirstColumn?: boolean;
    fixedLastColumn?: boolean;
    fixedHeader?: boolean;
    rowSelection?: {
        rowSelection: RowSelectionState;
        onRowSelectionChange: (updater: Updater<RowSelectionState>) => void;
        enableRowSelection?: boolean | ((row: Row<T>) => boolean);
    };
    manualFiltering?: boolean;
    manualSorting?: boolean;
    meta?: TableMeta<T>;
    emptyState?: EmptyStateProps;
    columnVisibility?: VisibilityState;
    getRowSubComponent?: (row: Row<T>) => ReactNode | undefined;
    showFooter?: boolean;

    /**
     * Shows border around the table itself (instead of having it "floating" in a page).
     * This is useful in case of virtualized tables, where the scrollbar is by default shown
     * in the table container; without a border, the scrollbar floats in a page without any
     * anchor.
     */
    showTableBorder?: boolean;

    /**
     * This controls how are grouped rows displayed.
     * - groupRows: The first non-grouped column is used to display the group values inside of it. The grouped columns are hidden.
     *
     * Currently it supports only single mode, "groupRows", but other modes could be potentially supported in future.
     * The props is used even with a single constant value, because the grouping requires multiple changes across the component;
     * using a single named prop ties these changes together.
     */
    groupDisplayMode?: 'groupRows';

    /**
     * Makes rows of the table virtualized, so only the visible ones are rendered in DOM.
     * Requires setting `maxHeight` to work.
     */
    virtualized?: boolean;

    maxHeight?: CSSProperties['maxHeight'];
    minHeight?: CSSProperties['minHeight'];
}

export const Table = <T, F extends TableFilters<T> = TableFilters<T>>({
    fixedFirstColumn = false,
    fixedLastColumn = false,
    fixedHeader = false,
    amountOfRows,
    data,
    loading: isLoading,
    rowSelection: rowSelectionObject,
    meta,
    uniqueRowId,
    emptyState,
    manualFiltering = false,
    manualSorting = false,
    columnVisibility,
    getRowSubComponent,
    groupDisplayMode = 'groupRows',
    showFooter = false,
    showTableBorder = false,
    virtualized = false,
    maxHeight,
    minHeight,
    ...other
}: TableProps<T>) => {
    const tableContainerRef = useRef<HTMLDivElement>(null);
    const { rowSelection, onRowSelectionChange, enableRowSelection } = rowSelectionObject || {};
    const rowSelectionEnabled = !!rowSelectionObject && enableRowSelection !== false;
    const { state, onStateChange } = useTableContext<T, F>();
    const isGroupingEnabled = state.grouping && state.grouping.length > 0;

    const sorting: TanstackColumnSort[] = useMemo(
        () =>
            Object.entries<ColumnSort | undefined>(state.sort).map(([key, colmnSort]) => ({
                id: key,
                desc: colmnSort?.order === Order.Descending,
            })),
        [state.sort]
    );

    const table = useReactTable({
        columns: useTableColumns<T>({ enableRowSelection: rowSelectionEnabled }),
        data,
        defaultColumn: {
            enableSorting: false,
            enableColumnFilter: false,
        },
        getCoreRowModel: getCoreRowModel(),
        getGroupedRowModel: isGroupingEnabled ? getGroupedRowModel() : undefined,
        getSortedRowModel: getSortedRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        getRowCanExpand: getRowSubComponent ? () => true : undefined,
        enableRowSelection,
        getRowId: (row) => row[uniqueRowId] as string,
        manualFiltering,
        manualSorting,
        meta,
        onRowSelectionChange,
        state: {
            rowSelection,
            columnVisibility,
            sorting,
            expanded: state.expanded,
            grouping: state.grouping,
        },
        onExpandedChange: (updaterOrValue) =>
            onStateChange({
                ...state,
                expanded: typeof updaterOrValue === 'function' ? updaterOrValue(state.expanded) : updaterOrValue,
            }),
        onGroupingChange: (newGrouping) =>
            onStateChange({
                ...state,
                grouping: typeof newGrouping === 'function' ? newGrouping(state.grouping || []) : newGrouping,
            }),
        groupedColumnMode: groupDisplayMode === 'groupRows' ? 'remove' : 'reorder',
    });

    const { t } = useLocalization();

    const pageCount = Math.ceil(amountOfRows / state.pagination.pageSize) ?? -1;
    const hasData = data.length > 0;

    const renderEmpty = () => {
        if (hasData) {
            return null;
        }
        return !isLoading ? (
            <EmptyState
                {...emptyState}
                primaryText={emptyState?.primaryText ?? t(OptimusTextKey.TableEmptyStatePrimary)}
            />
        ) : (
            <Container padding={['900', '0']} />
        );
    };

    const renderSubcomponent = (row: Row<T>) => {
        if (getRowSubComponent && row.getCanExpand() && row.getIsExpanded()) {
            const component = getRowSubComponent(row);
            if (component) {
                return (
                    <tr>
                        <td colSpan={row.getVisibleCells().length}>{component}</td>
                    </tr>
                );
            }
        }
        return null;
    };

    const { rows } = table.getRowModel();

    return (
        <TableWrapper aria-disabled={isLoading} {...other}>
            <TableContainer
                showTableBorder={showTableBorder}
                style={{
                    maxHeight,
                    minHeight,
                }}
                ref={tableContainerRef}
            >
                <TableElement>
                    <TableHeader fixedHeader={fixedHeader}>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableHeaderRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => {
                                    const sortable = header.column.getCanSort();
                                    const filterable = header.column.getCanFilter();

                                    return (
                                        <TableHeaderCell
                                            key={header.id}
                                            colSpan={header.colSpan}
                                            bottom={header.subHeaders.length === 0}
                                            fixedFirstColumn={fixedFirstColumn}
                                            fixedLastColumn={fixedLastColumn}
                                            fixedHeader={fixedHeader}
                                            rowSelectionEnabled={rowSelectionEnabled}
                                            width={header.column.columnDef.meta?.width}
                                            interactive={sortable || filterable}
                                            filterable={filterable}
                                            data-test-column-id={header.column.id}
                                        >
                                            {header.isPlaceholder ? null : (
                                                <TableColumnHeaderActions
                                                    header={header}
                                                    align={header.column.columnDef.meta?.align}
                                                    setRowSelection={onRowSelectionChange}
                                                    sortable={sortable}
                                                    filterable={filterable}
                                                />
                                            )}
                                        </TableHeaderCell>
                                    );
                                })}
                            </TableHeaderRow>
                        ))}
                    </TableHeader>
                    {virtualized ? (
                        <VirtualTableBody
                            table={table}
                            rows={rows}
                            getScrollElement={() => tableContainerRef.current}
                            isLoading={isLoading}
                            groupDisplayMode={groupDisplayMode}
                            rowSelection={rowSelection}
                            rowSelectionEnabled={rowSelectionEnabled}
                            fixedFirstColumn={fixedFirstColumn}
                            fixedLastColumn={fixedLastColumn}
                            renderSubcomponent={renderSubcomponent}
                        />
                    ) : (
                        <TableBody isLoading={isLoading}>
                            {rows.map((row, index) => {
                                const isActive = rowSelection && row.id in rowSelection;
                                const nextIsActive = rowSelection && rows[index + 1]?.id in rowSelection;
                                const previousIsActive = rowSelection && rows[index - 1]?.id in rowSelection;

                                return (
                                    <TableRow
                                        key={row.id}
                                        table={table}
                                        row={row}
                                        groupDisplayMode={groupDisplayMode}
                                        rowSelectionEnabled={rowSelectionEnabled}
                                        active={isActive}
                                        nextActive={nextIsActive}
                                        prevActive={previousIsActive}
                                        fixedFirstColumn={fixedFirstColumn}
                                        fixedLastColumn={fixedLastColumn}
                                        renderSubcomponent={renderSubcomponent}
                                    />
                                );
                            })}
                        </TableBody>
                    )}
                    {hasData && showFooter ? (
                        <TableFooter>
                            <TableFooterRow>
                                {table
                                    .getFooterGroups()
                                    // We want to render only leaf heders in footer, not all the groups
                                    .at(0)
                                    ?.headers.map((header) => {
                                        return (
                                            <TableFooterCell key={header.id} colSpan={header.colSpan}>
                                                <TableCellInner align={header.column.columnDef.meta?.align}>
                                                    {header.isPlaceholder
                                                        ? null
                                                        : flexRender(
                                                              header.column.columnDef.footer,
                                                              header.getContext()
                                                          )}
                                                </TableCellInner>
                                            </TableFooterCell>
                                        );
                                    })}
                            </TableFooterRow>
                        </TableFooter>
                    ) : null}
                </TableElement>
                {renderEmpty()}
            </TableContainer>

            {state.pagination.show && Number.isFinite(state.pagination.pageSize) && pageCount > 1 ? (
                <TablePagination size={pageCount} totalCount={amountOfRows} />
            ) : null}

            {isLoading ? (
                <LoadingOverlay show={isLoading} data-test-id="table-loading-state" zIndex={ZIndex.High}>
                    <Spinner />
                </LoadingOverlay>
            ) : null}
            <PrintStyles enableRowSelection={rowSelectionEnabled} />
        </TableWrapper>
    );
};

interface StandaloneTableProps<T, F extends TableFilters<T> = TableFilters<T>> extends TableProps<T> {
    columns: ColumnDef<T, any>[]; // eslint-disable-line @typescript-eslint/no-explicit-any
    state: TableState<T, F>;
    onStateChange: (state: TableState<T, F>) => void;
}

export const StandaloneTable = <T, F extends TableFilters<T>>(props: StandaloneTableProps<T, F>) => (
    <TableProvider columns={props.columns} state={props.state} onStateChange={props.onStateChange}>
        <Table {...props} />
    </TableProvider>
);
